import React, { useMemo, useRef, useState } from 'react';
import { Alert, ButtonBase, CircularProgress, IconButton, Snackbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { Box } from './DigitalTwinShared';
import { userMessage } from '../../utils/cachedResource';
import {
  DISEASE_SURVEY,
  DiseaseKey,
  DiseaseSection,
  SurveyAnswers,
  SurveyQuestion,
  countAllAnswered,
  countAnswered,
  findUnanswered,
  flattenQuestions,
  submitDiseaseSurvey,
} from './diseaseSurvey';

type DiseaseSurveySheetProps = {
  open: boolean;
  onClose: () => void;
  answers: SurveyAnswers;
  /**
   * 반드시 함수형 업데이트를 받는다. 이전 상태를 prop 에서 읽어 만들면
   * 한 렌더 안에서 두 문항을 연속 선택할 때 앞선 선택이 덮어써진다(실측 확인).
   */
  onChangeAnswers: (updater: (prev: SurveyAnswers) => SurveyAnswers) => void;
  /** 저장 성공 시 서버가 발급한 설문 ID 를 전달 */
  onSaved?: (surveyId: number) => void;
};

const ACCENT = '#6e8df0';
const ACCENT_DARK = '#5a78d8';
const TITLE_COLOR = '#2A4268';

/** 터치 목표 최소 높이 — iOS 44pt / Android 48dp 권장을 만족시킨다 */
const OPTION_HEIGHT = 54;

/**
 * 선택지 배치 결정.
 *  - 2개('예/아니오')            → 2열
 *  - 3개이고 라벨이 짧음(있음/없음/모름) → 3열
 *  - 그 외(리커트 5점, 긴 문구)  → 1열 full-width
 * 휴대폰에서 한 손 엄지로 누르는 것을 기준으로 잡았다.
 */
const columnsFor = (question: SurveyQuestion): number => {
  if (question.options.length === 2) return 2;
  if (question.options.length === 3 && question.options.every((o) => o.label.length <= 4)) return 3;
  return 1;
};

const QuestionBlock = ({
  question,
  value,
  onSelect,
}: {
  question: SurveyQuestion;
  value?: number;
  onSelect: (value: number) => void;
}) => {
  const cols = columnsFor(question);
  const answered = value !== undefined;

  return (
    <Box
      data-qcode={question.code}
      sx={{
        bgcolor: '#fff',
        borderRadius: '16px',
        p: '15px 14px 14px',
        border: '1px solid',
        borderColor: answered ? 'transparent' : '#dbe3ec',
      }}
    >
      {/* 문항 */}
      <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start', mb: 1.4 }}>
        <Box
          sx={{
            flexShrink: 0,
            minWidth: 32,
            height: 24,
            px: 0.7,
            borderRadius: '8px',
            bgcolor: answered ? ACCENT : '#e7ecf3',
            color: answered ? '#fff' : '#7d8a9c',
            fontSize: 12.5,
            fontWeight: 800,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {question.no}
        </Box>
        <Typography sx={{ fontSize: 15.5, color: '#25303d', lineHeight: 1.45, fontWeight: 600 }}>
          {question.text}
        </Typography>
      </Box>

      {/*
        선택지 — MUI Radio 대신 큰 버튼을 쓴다.
        Radio 는 지름 20px 원이 실질 터치 목표라 휴대폰에서 빗나가기 쉽다.
        role/aria 를 직접 붙여 라디오 그룹 의미와 키보드 조작은 유지한다.
      */}
      <Box
        role="radiogroup"
        aria-label={question.text}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: '8px',
        }}
      >
        {question.options.map((option) => {
          const selected = value === option.value;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(option.value)}
              sx={{
                minHeight: OPTION_HEIGHT,
                px: 1.2,
                py: 1,
                borderRadius: '13px',
                border: '1.5px solid',
                borderColor: selected ? ACCENT : '#dde4ed',
                bgcolor: selected ? ACCENT : '#fbfcfe',
                color: selected ? '#fff' : '#4a5568',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color .12s ease, border-color .12s ease',
                '&:active': { bgcolor: selected ? ACCENT_DARK : '#eef2f8' },
              }}
            >
              {/*
                선택 표시는 배경 채움 + 글자 두께 + 테두리색. 아이콘을 쓰지 않으므로
                색만으로 구분되지 않도록 fontWeight 를 함께 바꾼다. 보조기기에는
                위쪽 aria-checked 로 전달된다.
              */}
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: selected ? 700 : 500,
                  lineHeight: 1.35,
                  color: 'inherit',
                  textAlign: 'center',
                }}
              >
                {option.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

const DiseaseSurveySheet: React.FC<DiseaseSurveySheetProps> = ({
  open,
  onClose,
  answers,
  onChangeAnswers,
  onSaved,
}) => {
  const [activeKey, setActiveKey] = useState<DiseaseKey>(DISEASE_SURVEY[0].key);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ severity: 'error' | 'warning'; text: string } | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const section = useMemo<DiseaseSection>(
    () => DISEASE_SURVEY.find((s) => s.key === activeKey) ?? DISEASE_SURVEY[0],
    [activeKey],
  );

  const total = countAllAnswered(answers);
  const complete = total.answered === total.total;
  const activeIndex = DISEASE_SURVEY.findIndex((s) => s.key === activeKey);
  const isLastSection = activeIndex === DISEASE_SURVEY.length - 1;
  const sectionProgress = countAnswered(section, answers);
  const sectionDone = sectionProgress.answered === sectionProgress.total;

  /** 선택 직후 다음 미응답 문항으로 스크롤 — 휴대폰에서 손으로 밀어 찾는 수고를 줄인다 */
  const scrollToNextUnanswered = (given: Record<string, number>) => {
    const next = flattenQuestions(section).find((q) => given[q.code] === undefined);
    if (!next || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-qcode="${next.code}"]`);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const handleSelect = (question: SurveyQuestion, value: number) => {
    onChangeAnswers((prev) => ({
      ...prev,
      [section.key]: { ...(prev[section.key] ?? {}), [question.code]: value },
    }));
    // 스크롤 목표는 화면에 보이는 현재 값 기준으로 잡아도 충분하다
    scrollToNextUnanswered({ ...(answers[section.key] ?? {}), [question.code]: value });
  };

  const goSection = (key: DiseaseKey) => {
    setActiveKey(key);
    // 탭을 바꾸면 목록 맨 위부터 보이게
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const handleSave = async () => {
    const unanswered = findUnanswered(answers);
    if (unanswered.length > 0) {
      const first = unanswered[0];
      goSection(first.diseaseKey);
      setToast({
        severity: 'warning',
        text: `미응답 ${unanswered.length}문항이 있습니다. ${first.diseaseLabel} ${first.no}번부터 확인해 주세요.`,
      });
      return;
    }

    setSaving(true);
    try {
      const surveyId = await submitDiseaseSurvey(answers);
      // 성공 알림은 부모(화면 1)가 띄운다 — 문진은 1회성이라 저장되면 시트가 닫히기 때문
      onSaved?.(surveyId);
    } catch (err: any) {
      // 저장 실패를 조용히 넘기지 않는다 — 건강 데이터는 유실되면 사용자가 알 수 없다.
      // 다만 내부 오류 문구(인터셉터가 만드는 영문 TypeError 등)는 노출하지 않는다.
      setToast({
        severity: 'error',
        text: userMessage(err, '문진 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* 백드롭 */}
      <Box
        onClick={saving ? undefined : onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,.38)',
          zIndex: 1200,
          animation: 'fadeIn .2s ease',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* BottomSheet — 폼이 길어 스크롤이 필요하므로 화면 4와 달리 높이 고정 */}
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 430,
          height: '94vh',
          zIndex: 1300,
          bgcolor: '#eef2f6',
          borderRadius: '22px 22px 0 0',
          boxShadow: '0 -8px 24px rgba(80, 98, 107, .22)',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflow: 'hidden',
          animation: 'slideUp .26s cubic-bezier(.32,.72,0,1)',
          '@keyframes slideUp': {
            from: { transform: 'translate(-50%, 100%)' },
            to: { transform: 'translate(-50%, 0)' },
          },
        }}
      >
        {/* 헤더 */}
        <Box sx={{ flexShrink: 0, p: '14px 16px 0' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: TITLE_COLOR, lineHeight: 1.25 }}>
                4대 질병 건강 문진
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: '#7d8a9c', mt: 0.2 }}>
                {`답변 ${total.answered} / ${total.total}문항 · 한 번만 작성합니다`}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={saving}
              aria-label="문진 닫기"
              sx={{ color: '#8c9aab', mr: -1, mt: -0.5, width: 44, height: 44 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* 전체 진행률 */}
          <Box sx={{ mt: 1.1, height: 6, borderRadius: '99px', bgcolor: '#dde5ee', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${total.total === 0 ? 0 : (total.answered / total.total) * 100}%`,
                bgcolor: ACCENT,
                borderRadius: '99px',
                transition: 'width .25s ease',
              }}
            />
          </Box>

          {/* 질환 탭 */}
          <Box
            sx={{
              mt: 1.3,
              display: 'grid',
              gridTemplateColumns: `repeat(${DISEASE_SURVEY.length}, 1fr)`,
              gap: '6px',
            }}
          >
            {DISEASE_SURVEY.map((s) => {
              const progress = countAnswered(s, answers);
              const done = progress.answered === progress.total;
              const active = s.key === activeKey;
              return (
                <ButtonBase
                  key={s.key}
                  onClick={() => goSection(s.key)}
                  aria-current={active}
                  sx={{
                    flexDirection: 'column',
                    minHeight: 48,
                    borderRadius: '12px',
                    px: 0.5,
                    bgcolor: active ? ACCENT : '#fff',
                    color: active ? '#fff' : '#5a6b80',
                    boxShadow: active ? '0 3px 10px rgba(110,141,240,.3)' : 'none',
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      mt: '2px',
                      lineHeight: 1.2,
                      color: active ? 'rgba(255,255,255,.9)' : done ? '#2f9e63' : '#98a5b6',
                      fontWeight: done ? 700 : 400,
                    }}
                  >
                    {done ? '완료' : `${progress.answered}/${progress.total}`}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        {/* 문항 목록 — 대분류 번호별로 묶어서 표시 */}
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: '14px 16px 20px',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          {section.groups.map((group) => (
            <Box key={`${section.key}-${group.groupNo}`} sx={{ mb: 2.4 }}>
              <Typography
                sx={{ fontSize: 12, fontWeight: 800, color: '#8c9aab', letterSpacing: '.04em', mb: 0.9 }}
              >
                {`문항 ${group.groupNo}`}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {group.questions.map((question) => (
                  <QuestionBlock
                    key={question.code}
                    question={question}
                    value={answers[section.key]?.[question.code]}
                    onSelect={(value) => handleSelect(question, value)}
                  />
                ))}
              </Box>
            </Box>
          ))}

          <Typography sx={{ fontSize: 11.5, color: '#9aa6b2', lineHeight: 1.6, textAlign: 'center', mt: 1 }}>
            {'문진 결과는 디지털트윈 위험도 산출의 보조 지표로만 사용됩니다.\n진단이 아니며 정확한 진단은 의료 전문가와 상담해 주세요.'}
          </Typography>
        </Box>

        {/*
          푸터. 하단 홈 인디케이터 여백은 앱 래퍼의 SafeAreaView 가 네이티브에서 처리한다
          (public/index.html 의 viewport 에 viewport-fit=cover 가 없어 env() 는 0 으로 해석됨 —
          웹 단독 접속까지 대비해 남겨두지만 실제 값은 앱 쪽에서 온다).
        */}
        <Box
          sx={{
            flexShrink: 0,
            p: '10px 16px calc(16px + env(safe-area-inset-bottom))',
            bgcolor: '#eef2f6',
            borderTop: '1px solid #e0e7ef',
            display: 'flex',
            gap: '8px',
          }}
        >
          {!isLastSection ? (
            <>
              <ButtonBase
                onClick={() => goSection(DISEASE_SURVEY[activeIndex + 1].key)}
                sx={{
                  flex: 1,
                  minHeight: 54,
                  borderRadius: '15px',
                  bgcolor: sectionDone ? ACCENT : '#fff',
                  color: sectionDone ? '#fff' : ACCENT,
                  border: sectionDone ? 'none' : `1.5px solid ${ACCENT}`,
                  fontSize: 16,
                  fontWeight: 800,
                  boxShadow: sectionDone ? '0 4px 12px rgba(110,141,240,.35)' : 'none',
                  '&:active': { bgcolor: sectionDone ? ACCENT_DARK : '#eef2f8' },
                }}
              >
                {`다음 · ${DISEASE_SURVEY[activeIndex + 1].label}`}
              </ButtonBase>
              {complete && (
                <ButtonBase
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    width: 104,
                    minHeight: 54,
                    borderRadius: '15px',
                    bgcolor: ACCENT,
                    color: '#fff',
                    fontSize: 15.5,
                    fontWeight: 800,
                  }}
                >
                  {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '저장'}
                </ButtonBase>
              )}
            </>
          ) : (
            <ButtonBase
              onClick={handleSave}
              disabled={saving}
              sx={{
                flex: 1,
                minHeight: 54,
                borderRadius: '15px',
                bgcolor: complete ? ACCENT : '#c3cddb',
                color: '#fff',
                fontSize: 16.5,
                fontWeight: 800,
                boxShadow: complete ? '0 4px 12px rgba(110,141,240,.35)' : 'none',
                '&:active': complete ? { bgcolor: ACCENT_DARK } : {},
              }}
            >
              {saving ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : complete ? (
                '문진 저장'
              ) : (
                `미응답 ${total.total - total.answered}문항`
              )}
            </ButtonBase>
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ fontSize: 13.5 }}>
          {toast?.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DiseaseSurveySheet;
